import { useStore } from "../../lib/store";
import { useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Loader from "../../components/Loader";
import {
  pendingBroadcastClickStore,
  pendingPushOpenStore,
} from "../../domains/notification/usePushOpenTracking";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getUserStartingPage } from "../../domains/user/utils/getUserStartingPage";
import { apiClient, type Api } from "../../services/api";
import {
  AuthTokenUnavailableError,
  ClientError,
} from "../../../../api/src/shared/request";
import { openExternalUrl } from "../../tools/open-external-url";
import { replaceInPlace } from "../../domains/app/utils/backNavigation";
import {
  NotificationType,
  PUSH_ENTRY_PARAM,
  PUSH_ENTRY_VALUE,
} from "../../../../api/src/shared/domain/notification";
import { JUISCI_APP_HOSTS } from "../../../../api/src/shared/domain/notification/link-target";

// Personal notifications resolve via the private endpoint.
// Broadcasts resolve privately for authenticated users, publicly otherwise.
type FormattedPersonalNotification =
  Api["get"]["/webapp/private/notification/personals/:id"]["output"]["body"];

function openExternalLink(url: string, history: ReturnType<typeof useHistory>) {
  replaceInPlace(history, "/profile/notifications");
  openExternalUrl(url);
}

/**
 * Backoff for "the auth token isn't readable yet".
 *
 * `getBearerToken` throws `AuthTokenUnavailableError` BEFORE the request leaves
 * the client when no token can be read — a cold webview launch, or WKWebView
 * evicting IndexedDB on a native reload. That is not a `ClientError`, so the 401
 * branch never sees it, and it is not "no such notification" either: a moment
 * later the token is usually there.
 *
 * Retried HERE, inside the one async pass, rather than by un-latching and waiting
 * for a re-render: `currentUser` is only non-null while the `/users/me` query is
 * in `success`, so if that query errors it stays null forever, no dependency of
 * `loadNotification` ever changes, and a re-render-based retry would leave the
 * user on the loader indefinitely. This terminates by construction.
 */
const TOKEN_RETRY_DELAYS_MS = [150, 400, 1000];

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Resolve a personal/workflow notification, tolerating a token that is not
 * readable yet. Returns `"auth"` when the server rejected the session (the global
 * handler owns the login redirect), or `null` when it is genuinely unresolvable —
 * including after the retries are exhausted, so the caller falls back to the
 * notification centre rather than hanging.
 */
async function fetchPersonalAwaitingToken(
  notificationId: string
): Promise<FormattedPersonalNotification | "auth" | null> {
  for (const [attempt, delay] of [0, ...TOKEN_RETRY_DELAYS_MS].entries()) {
    if (delay > 0) await sleep(delay);

    const result = await apiClient
      .get("/webapp/private/notification/personals/:id", {
        param: { id: notificationId },
      })
      .then((res) => res.body)
      .catch((error: unknown) => {
        // 401 handled globally by apiClient.onRequestError (caches URL, redirects to login)
        if (error instanceof ClientError && error.responseStatus === 401)
          return "auth" as const;
        if (error instanceof AuthTokenUnavailableError) return "retry" as const;
        console.log("Could not load personal notification", error);
        return null;
      });

    if (result !== "retry") return result;
    if (attempt === TOKEN_RETRY_DELAYS_MS.length)
      console.log(
        "Auth token never became readable; falling back to the notification centre"
      );
  }
  return null;
}

// NOTE: This page takes a notification and redirects to the related page.

export default function NotificationRouter({ id }: { id: string }) {
  const history = useHistory();
  const notificationId = id;
  const { activeLang } = useActiveLang();
  const { currentUser } = useCurrentUser();
  const [, setPendingBroadcastClick] = useStore(pendingBroadcastClickStore);
  const [, setPendingPushOpen] = useStore(pendingPushOpenStore);
  const location = useLocation();
  const processedRef = useRef(false);

  // Marks the entry as a notification-banner tap rather than a tap on the same
  // notification inside the in-app center — the api counts the two as different
  // metrics. Set by `notificationLink()` on the server, so it is on EVERY push
  // regardless of type.
  const isPushEntry =
    new URLSearchParams(location.search).get(PUSH_ENTRY_PARAM) ===
    PUSH_ENTRY_VALUE;

  const redirectBroadcast = useCallback(
    async (broadcast: { link: string | null; room: string | null }) => {
      const { link, room } = broadcast;

      setPendingBroadcastClick((pending) =>
        pending.includes(notificationId)
          ? pending
          : [...pending, notificationId]
      );

      if (link === null) {
        replaceInPlace(history, "/profile/notifications");
        return;
      }
      if (link === "/") {
        if (room !== null) {
          replaceInPlace(history, `/room/discovery/${room}`);
          return;
        }

        const startingPage =
          currentUser !== null
            ? await getUserStartingPage(currentUser)
            : "/discovery";
        replaceInPlace(history, startingPage);
        return;
      }

      if (JUISCI_APP_HOSTS.some((host) => link.includes(host))) {
        const path = link.split(".com").at(1);
        if (path !== undefined) {
          replaceInPlace(history, path);
          return;
        }
        replaceInPlace(history, "/profile/notifications");
        return;
      }
      openExternalLink(link, history);
    },
    [currentUser, history, notificationId, setPendingBroadcastClick]
  );

  /** Redirect for personal notifications after broadcast resolution is exhausted. */
  const redirect = useCallback(
    (notification: FormattedPersonalNotification) => {
      switch (notification.type) {
        case NotificationType.LemonCertifiedAnswered: {
          replaceInPlace(history, "/settings");
          return;
        }

        case NotificationType.ArticlePublished: {
          replaceInPlace(
            history,
            `/post/${notification.article.slug}?notificationCenter=true`
          );
          return;
        }

        case NotificationType.VideoPublished: {
          const { slug, videoFormat } = notification.content;
          replaceInPlace(
            history,
            videoFormat === "story" ? `/video/story/${slug}` : `/video/${slug}`
          );
          return;
        }

        case NotificationType.TestNotification: {
          replaceInPlace(history, "/profile/notifications");
          return;
        }

        case NotificationType.PlaylistShared: {
          replaceInPlace(
            history,
            `/profile/playlist/${notification.playlist.id}`
          );
          return;
        }

        case NotificationType.VisualAbstractReady: {
          replaceInPlace(
            history,
            `/visual-abstract/${notification.visualAbstract.id}/preview`
          );
          return;
        }

        case NotificationType.Workflow: {
          // The destination is an arbitrary link resolved by the workflow at send
          // time and stored per language on the contents — same slot the title
          // and text come from. No link (the workflow sent none) → the
          // notification center, which is where the message is readable anyway.
          const localized = notification.contents[activeLang];
          const link =
            localized === null || localized.link === null
              ? notification.contents.en.link
              : localized.link;
          if (link === null) {
            replaceInPlace(history, "/profile/notifications");
            return;
          }
          if (JUISCI_APP_HOSTS.some((host) => link.includes(host))) {
            const path = link.split(".com").at(1);
            replaceInPlace(
              history,
              path === undefined ? "/profile/notifications" : path
            );
            return;
          }
          if (link.startsWith("/")) {
            replaceInPlace(history, link);
            return;
          }
          openExternalLink(link, history);
          return;
        }

        default: {
          throw new Error(notification satisfies never);
        }
      }
    },
    [history, activeLang]
  );

  const loadNotification = useCallback(async () => {
    // Prevent double-processing when callback is recreated (e.g., currentUser changes)
    if (processedRef.current) return;
    processedRef.current = true;

    // Enqueued BEFORE resolving the notification: the open happened whatever the
    // notification turns out to be, and it must survive a resolution that 404s or
    // bounces through login. The api de-duplicates per (notification, user).
    if (isPushEntry) {
      setPendingPushOpen((pending) =>
        pending.includes(notificationId)
          ? pending
          : [...pending, notificationId]
      );
    }

    if (currentUser === null) {
      const publicBroadcastRes = await apiClient
        .get("/webapp/public/notifications/:id", {
          param: { id: notificationId },
          query: { lang: activeLang },
        })
        .catch((error: unknown) => {
          // probably personal notification if 404
          if (error instanceof ClientError && error.responseStatus === 404)
            return null;
          // real error
          console.error(error);
          return false;
        });

      if (publicBroadcastRes) {
        await redirectBroadcast(publicBroadcastRes.body);
        return;
      }
    } else {
      const privateBroadcastRes = await apiClient
        .get("/webapp/private/notification/broadcasts/:id", {
          param: { id: notificationId },
        })
        .catch((error: unknown) => {
          if (error instanceof ClientError && error.responseStatus === 404)
            return null;
          if (error instanceof ClientError && error.responseStatus === 401)
            return "auth";
          console.error(error);
          return false;
        });

      if (privateBroadcastRes === "auth") return;
      if (privateBroadcastRes) {
        const body = privateBroadcastRes.body;
        if (body.type === "Broadcast" || body.type === "ExternalLink") {
          await redirectBroadcast(body);
        } else {
          await redirectBroadcast({ link: null, room: null });
        }
        return;
      }
    }

    // Not a broadcast - try personal (requires auth)
    const personalRes = await fetchPersonalAwaitingToken(notificationId);

    if (personalRes === "auth") return;
    if (personalRes === null) {
      replaceInPlace(history, "/profile/notifications");
      return;
    }

    void apiClient
      .post("/webapp/private/notification/personals/:id/clicked", {
        param: { id: notificationId },
      })
      .catch(console.error);

    redirect(personalRes);
  }, [
    notificationId,
    redirect,
    redirectBroadcast,
    history,
    activeLang,
    currentUser,
    isPushEntry,
    setPendingPushOpen,
  ]);

  useEffect(() => {
    loadNotification();
  }, [loadNotification]);

  return <Loader expanded />;
}
